import { createFileRoute, Link, useLocation } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowRight,
  Award,
  Building2,
  ChevronDown,
  ChevronUp,
  Compass,
  Eye,
  Flag,
  Handshake,
  Heart,
  LineChart,
  MapPin,
  Milestone,
  Sparkles,
  Target,
  Users,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ReadBioButton } from "@/components/site/LeaderBioDialog";
import { Container, CardMotion } from "@/components/site/Primitives";
import { SectionRail } from "@/components/site/SectionRail";
import { CraftRenderer } from "@/components/craft/CraftRenderer";
import { hasCraftContent } from "@/lib/craft/serialize";
import {
  aboutBackground,
  coreValues,
  heritageTimeline,
  visionMission,
  whyChooseUs,
} from "@/lib/content";
import type { Leader } from "@/lib/types/content";
import { loadLeaders, loadPageBySlug } from "@/lib/content-loader";
import aboutHeritage from "@/assets/about-heritage.jpg";
import penangSkyline from "@/assets/penang-gurney-waterfront-golden.png.asset.json";
import penangKomtar from "@/assets/ksl-boardroom-daylight.png.asset.json";
import penangGeorgetown from "@/assets/penang-georgetown-street-golden.png.asset.json";
import familyGenerations from "@/assets/family-generations.png.asset.json";
import coreValuesWheel from "@/assets/core-values-wheel.png.asset.json";




export const Route = createFileRoute("/about")({
  loader: async () => ({
    leaders: await loadLeaders(),
    cmsPage: await loadPageBySlug("about"),
  }),
  head: () => ({
    meta: [
      { title: "About KS Lau & Co — Heritage, Vision and Leadership" },
      { name: "description", content: "Established in 1978, KS Lau & Co has guided generations of businesses through Malaysia's evolving regulatory landscape with technical depth and personal service." },
      { property: "og:title", content: "About KS Lau & Co" },
      { property: "og:description", content: "Heritage, vision, values and leadership of a chartered accountancy firm with over 45 years of practice." },
      { property: "og:image", content: aboutHeritage },
      { name: "twitter:image", content: aboutHeritage },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  const { leaders, cmsPage } = Route.useLoaderData();
  const location = useLocation();
  const hash = location.hash;
  const initialTab =
    hash === "leadership" || hash === "alan-lau" || hash === "lau-kim-seong"
      ? "leadership"
      : "company";
  const [tab, setTab] = useState<string>(initialTab);

  if (cmsPage && hasCraftContent(cmsPage.craftState as string | object)) {
    return (
      <CraftRenderer
        craftState={cmsPage.craftState}
        craftCustomCss={cmsPage.craftCustomCss}
      />
    );
  }

  return (
    <>
      {/* Hero band */}
      <section className="relative isolate overflow-hidden">
        <img
          src={penangKomtar.url}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 -z-20 h-full w-full object-cover"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-gradient-to-t from-primary/75 via-primary/40 to-primary/10 md:bg-gradient-to-r md:from-primary/70 md:via-primary/35 md:to-primary/5"
        />
        <Container>
          <div className="flex min-h-[32vh] flex-col justify-end py-10 text-primary-foreground md:min-h-[40vh] md:py-14">
            <h1 className="max-w-3xl text-3xl font-semibold leading-[1.15] tracking-tight md:text-4xl lg:text-5xl">
              More Than 45 Years
              <br />
              of Trusted Advice
            </h1>
          </div>
        </Container>
      </section>

      {/* Tabs */}
      <section className="relative py-8 md:py-12">
        {tab === "company" && (
          <SectionRail
            sections={[
              { id: "about-story", label: "Our Story" },
              { id: "about-journey", label: "Our Journey" },
              { id: "about-why", label: "Why Us" },
              { id: "about-vision", label: "Vision & Mission" },
              { id: "about-values", label: "Core Values" },
              { id: "about-cta", label: "Get in Touch" },
            ]}
          />
        )}
        <Container>
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="h-auto rounded-xl bg-transparent p-0">
              <TabsTrigger
                value="company"
                className="rounded-xl border-b-2 border-transparent bg-transparent px-5 py-3 text-base font-medium text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none"
              >
                About the Firm
              </TabsTrigger>
              <TabsTrigger
                value="leadership"
                className="rounded-xl border-b-2 border-transparent bg-transparent px-5 py-3 text-base font-medium text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none"
              >
                Leadership
              </TabsTrigger>
            </TabsList>

            <TabsContent value="company" className="mt-8 space-y-12 md:space-y-16">
              <CompanyTab onSwitchToLeadership={() => setTab("leadership")} />
            </TabsContent>

            <TabsContent value="leadership" id="leadership" className="mt-8 space-y-12 md:space-y-16">
              <LeadershipTab leaders={leaders} />
            </TabsContent>
          </Tabs>
        </Container>
      </section>
    </>
  );
}

/* ---------------- Shared bits ---------------- */

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm font-semibold uppercase tracking-[0.22em] text-gold">{children}</p>
  );
}

function SerifHeading({
  children,
  className = "",
  oneLine = false,
}: {
  children: React.ReactNode;
  className?: string;
  oneLine?: boolean;
}) {
  const size = oneLine
    ? "text-2xl md:text-3xl lg:text-[2rem] lg:whitespace-nowrap"
    : "text-2xl md:text-3xl lg:text-4xl";
  return (
    <h2 className={`leading-[1.15] tracking-tight text-primary ${size} ${className}`}>
      {children}
    </h2>
  );
}

function RedRule(_: { className?: string }) {
  return null;
}

/* ---------------- Company tab ---------------- */

const journeyIcons = [Building2, Milestone, Sparkles, Compass, Flag];
const whyIcons = [Award, MapPin, Handshake, LineChart];



function CompanyTab({ onSwitchToLeadership }: { onSwitchToLeadership: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? aboutBackground : aboutBackground.slice(0, 2);
  const hasMore = aboutBackground.length > 2;
  return (
    <>
      {/* Our Story */}
      <section id="about-story" className="scroll-mt-28 grid items-center gap-8 md:grid-cols-2 md:gap-10">
        <div>
          <SerifHeading>A Legacy Built on Trust</SerifHeading>
          <RedRule className="mt-4" />
          <div className="mt-5 space-y-4 text-base leading-relaxed text-foreground/85 md:text-base text-justify">
            {visible.map((p, i) => (
              <p key={i} className="text-justify">{p}</p>
            ))}
          </div>
          {hasMore && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.16em] text-gold transition hover:text-primary"
            >
              {expanded ? "Show less" : "Read more"}
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          )}
        </div>
        <div className="relative">
          <img
            src={penangGeorgetown.url}
            alt="Heritage of KS Lau & Co in Penang"
            loading="lazy"
            className="aspect-[4/3] w-full object-cover shadow-xl"
          />
        </div>
      </section>


      {/* Our Journey */}
      <section id="about-journey" className="scroll-mt-28">
        <Eyebrow>Our Journey</Eyebrow>
        <ol className="relative mt-8 grid gap-8 md:grid-cols-5 md:gap-4">
          {/* Solid gold connector behind all icons */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-[10%] right-[10%] top-7 hidden h-px -translate-y-1/2 bg-gold md:block"
            style={{ zIndex: 0 }}
          />
          {/* Small gold dot markers between icons */}
          {heritageTimeline.slice(0, -1).map((_, i) => (
            <span
              key={`dot-${i}`}
              aria-hidden="true"
              className="pointer-events-none absolute top-7 hidden h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold md:block"
              style={{ left: `${((i + 1) / heritageTimeline.length) * 100}%`, zIndex: 1 }}
            />
          ))}
          {heritageTimeline.map((t, i) => {
            const Icon = journeyIcons[i] ?? Milestone;
            return (
              <li key={t.year} className="relative z-10 flex flex-col items-center text-center">
                <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <Icon className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <h3 className="mt-4 text-base font-semibold tracking-tight text-primary">{t.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.body}</p>
              </li>
            );
          })}
        </ol>
      </section>

      {/* Why Clients Choose Us */}
      <section id="about-why" className="scroll-mt-28">
        <div className="text-center">
          <SerifHeading oneLine>What Sets Our Firm Apart</SerifHeading>
          <RedRule className="mx-auto mt-5" />
        </div>
        <div className="mt-8 grid gap-8 md:grid-cols-3 md:gap-6">
          {whyChooseUs.map((w, i) => {
            const Icon = whyIcons[i] ?? Award;
            return (
              <div key={w.title} className="bg-background p-6 text-center">
                <Icon className="mx-auto h-8 w-8 text-primary" strokeWidth={1.5} />
                <h3 className="mt-4 text-base font-semibold tracking-tight text-primary">{w.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{w.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Vision & Mission */}
      <section id="about-vision" className="scroll-mt-28 bg-primary px-6 py-12 text-primary-foreground md:px-12 md:py-16">
        <div className="grid gap-10 md:grid-cols-2 md:gap-16">
          <div className="md:pr-10 md:border-r md:border-gold/40">
            <Eye className="h-8 w-8 text-gold" strokeWidth={1.5} />
            <p className="mt-4 text-sm font-semibold uppercase tracking-[0.22em] text-white/70">Vision</p>
            <p className="mt-3 text-xl leading-snug md:text-2xl">{visionMission.vision}</p>
          </div>
          <div>
            <Target className="h-8 w-8 text-gold" strokeWidth={1.5} />
            <p className="mt-4 text-sm font-semibold uppercase tracking-[0.22em] text-white/70">Mission</p>
            <p className="mt-3 text-xl leading-snug md:text-2xl">{visionMission.mission}</p>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section id="about-values" className="scroll-mt-28">
        <div className="grid items-center gap-10 md:grid-cols-12 md:gap-12">
          <div className="md:col-span-5">
            <img
              src={coreValuesWheel.url}
              alt="KS Lau & Co core values: Integrity, Trust, Impact, People and Excellence"
              loading="lazy"
              className="mx-auto w-full max-w-md drop-shadow-xl"
            />
          </div>
          <ul className="md:col-span-7 space-y-5">
            {coreValues.map((v, i) => {
              const dotColors = ["bg-[#2BAFB8]", "bg-[#7BB661]","bg-[#E8833A]", "bg-[#7B5BB6]","bg-[#1F4E8C]" ];
              return (
                <li key={v.name} className="flex gap-4">
                  <span aria-hidden="true" className={`mt-2 h-3 w-3 shrink-0 rounded-full ${dotColors[i] ?? "bg-primary"}`} />
                  <div>
                    <h3 className="text-lg font-semibold tracking-tight text-primary md:text-xl">{v.name}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-foreground/80 md:text-base">{v.body}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Closing CTA */}
      <section id="about-cta" className="scroll-mt-28 grid items-center gap-10 md:grid-cols-2 md:gap-14">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-primary md:text-3xl lg:text-4xl">
            For over four decades, businesses have entrusted us with their most important decisions.
          </h2>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-base font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              Contact Us
              <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              type="button"
              onClick={onSwitchToLeadership}
              className="inline-flex items-center gap-2 rounded-md border border-primary bg-transparent px-6 py-3 text-base font-semibold text-primary transition hover:bg-primary/5"
            >
              Meet Our Leadership
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
        <img
          src={familyGenerations.url}
          alt="Family generations"
          loading="lazy"
          width={1280}
          height={800}
          className="aspect-[16/10] w-full rounded-2xl object-cover shadow-xl"
        />
      </section>

      {/* By the Numbers */}
      <section className="-mt-4 bg-background pb-2 md:-mt-8">
        <Eyebrow>By the Numbers</Eyebrow>
        <div className="mt-5 grid grid-cols-2 gap-6 md:grid-cols-5 md:gap-8">
          {[
            { headline: "45+ Years", label: "serving businesses" },
            { headline: "Since 1978", label: "established in Penang" },
            { headline: "Multiple Generations", label: "of client relationships" },
            { headline: "10,000+", label: "of engagements completed" },
            { headline: "Diverse Industry Experience", label: "across Malaysia" },
          ].map((s) => (
            <div key={s.headline} className="text-center">
              <p className="text-xl font-medium leading-tight tracking-tight text-primary md:text-2xl">
                {s.headline}
              </p>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground md:text-sm">{s.label}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

/* ---------------- Leadership tab ---------------- */

const leadershipValues = [
  { icon: Award, title: "Experienced Leaders", body: "Decades of combined expertise across audit, tax and advisory." },
  { icon: Users, title: "Client-Focused", body: "Direct partner access and tailored, hands-on engagements." },
  { icon: Compass, title: "Future-Oriented", body: "Embracing technology, AI and digital transformation." },
  { icon: Heart, title: "People First", body: "Investing in our team and the communities we serve." },
];

function LeadershipTab({ leaders }: { leaders: Leader[] }) {
  return (
    <>
      {/* Intro */}
      <section className="mx-auto max-w-3xl text-center">
        <Eyebrow>Our Leadership</Eyebrow>
        <SerifHeading oneLine className="mt-4">Guiding with Vision. Serving with Integrity.</SerifHeading>
        <RedRule className="mx-auto mt-5" />
      </section>

      {/* Partner cards */}
      <section className="mx-auto mt-8 grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-5 lg:gap-6">
        {leaders.map((l, i) => (
          <LeaderCard key={l.slug} leader={l} animateIndex={i} />
        ))}
      </section>

      {/* Values strip */}
      <section className="grid gap-8 md:grid-cols-4 md:gap-6">
        {leadershipValues.map((v) => (
          <div key={v.title} className="flex flex-col items-center bg-background px-8 py-14 text-center md:px-10 md:py-20">
            <v.icon className="h-14 w-14 text-primary md:h-16 md:w-16" strokeWidth={1.5} />
            <h3 className="mt-8 text-xl font-semibold tracking-tight text-primary md:text-2xl">{v.title}</h3>
            <p className="mt-5 max-w-xs text-base leading-relaxed text-muted-foreground md:text-lg">{v.body}</p>
          </div>
        ))}
      </section>

      {/* Closing CTA band */}
      <section className="relative isolate overflow-hidden">
        <img
          src={penangKomtar.url}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 -z-20 h-full w-full object-cover"
        />
        <div aria-hidden="true" className="absolute inset-0 -z-10 bg-primary/90" />
        <div className="px-6 py-12 text-center text-primary-foreground md:px-12 md:py-16">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-white/80">Together, We Go Further</p>
          <h2 className="mx-auto mt-3 max-w-3xl text-2xl leading-[1.15] tracking-tight md:text-3xl lg:text-4xl">
            A Team United by Values. A Purpose Shared.
          </h2>
          <div className="mt-6">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md border border-white/70 px-6 py-2.5 text-base font-semibold uppercase tracking-[0.14em] text-white transition hover:bg-white hover:text-primary"
            >
              Speak to Our Team
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function LeaderCard({
  leader: l,
  className = "",
  animateIndex = 0,
}: {
  leader: Leader;
  className?: string;
  animateIndex?: number;
}) {
  return (
    <CardMotion
      animateIndex={animateIndex}
      id={l.slug}
      className={`scroll-mt-28 flex w-full flex-col overflow-hidden rounded-xl bg-background shadow-sm ${className}`}
    >
      <div className="aspect-[4/5] w-full overflow-hidden bg-muted">
        <img
          src={l.image}
          alt={`Portrait of ${l.name}`}
          loading="lazy"
          width={640}
          height={800}
          className="h-full w-full object-cover"
        />
      </div>
      <div className="flex flex-1 flex-col p-4 lg:p-5">
        <h3 className="text-base tracking-tight text-primary lg:text-lg">{l.name}</h3>
        <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-gold lg:text-sm">{l.designation}</p>
        <p className="mt-2 text-sm leading-relaxed text-foreground/85 line-clamp-3">{l.bio[0]}</p>
        <div className="mt-auto pt-3">
          <ReadBioButton leader={l} />
        </div>
      </div>
    </CardMotion>
  );
}
